import { useState } from 'react';
import type { ExportDestinationContent } from '@capture/shared';
import { isFormatlessAdapter } from '@capture/shared';
import { ADAPTER_OPTIONS, FORMAT_OPTIONS } from './destination-options';
import styles from './export-composer.module.css';

interface AddDestinationDialogProps {
  onAdd: (dest: ExportDestinationContent) => void;
  onCancel: () => void;
}

export function AddDestinationDialog({ onAdd, onCancel }: AddDestinationDialogProps) {
  const [name, setName] = useState('');
  const [adapter, setAdapter] = useState<ExportDestinationContent['adapter']>('skarn_product');
  const [format, setFormat] = useState<ExportDestinationContent['format']>('json');

  // E-047 residual (walk V9) — dossier / document_repository build their own
  // envelope/PDF and bind no ExportFormat at all; showing the picker here
  // invites an operator to "configure" a control that does nothing, same as
  // the E-036 fix on the detail page. `format` stays on the created record
  // (the type requires it and formatless adapters ignore it at export time)
  // but it is never user-driven for these adapters.
  const formatless = isFormatlessAdapter(adapter);

  function handleAdd() {
    if (!name.trim()) return;
    const dest: ExportDestinationContent = {
      id: crypto.randomUUID(),
      name: name.trim(),
      adapter,
      format,
      config: {},
      mappings: [],
    };
    onAdd(dest);
  }

  return (
    <div className={styles.dialogOverlay} role="dialog" aria-modal="true" aria-label="Add destination">
      <div className={styles.dialog}>
        <h2 className={styles.dialogTitle}>Add Export Destination</h2>

        <div className={styles.dialogField}>
          <label htmlFor="dest-name">Name</label>
          <input
            id="dest-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Destination name"
            autoFocus
          />
        </div>

        <div className={styles.dialogField}>
          <label htmlFor="dest-adapter">Adapter</label>
          <select
            id="dest-adapter"
            value={adapter}
            onChange={(e) => setAdapter(e.target.value as ExportDestinationContent['adapter'])}
          >
            {ADAPTER_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {!formatless && (
          <div className={styles.dialogField}>
            <label htmlFor="dest-format">Format</label>
            <select
              id="dest-format"
              value={format}
              onChange={(e) => setFormat(e.target.value as ExportDestinationContent['format'])}
            >
              {FORMAT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
        {/* E-036/E-047 — sql DOES bind a format but most destinations skip it
            in favor of inline column definition; same copy as the detail page. */}
        {adapter === 'sql' && (
          <p className={styles.formatAdapterHint} data-testid="sql-format-hint">
            A bound format supplies a fixed column list — most sql destinations
            skip it and use <strong>No template — define inline</strong>, defining
            columns directly in the Mapping tab instead.
          </p>
        )}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.dialogCancelBtn} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={styles.dialogAddBtn} onClick={handleAdd} disabled={!name.trim()}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
